import React from "react";
import { Link, Navigate } from "react-router-dom";
import PageHeader from "src/components/pageHeader";
import PageLoading from "src/components/pageLoading";
import Pagination from "src/components/pagination";
import usePagination from "src/customHooks/usePagination";
import RequestedCountriesTable from "src/features/karma-exchanges/components/requested-countries-table";
import {
  PopulatedRequestedCountry,
  useCountKarmaExchangeRequestedCountriesQuery,
  useGetKarmaExchangeRequestedCountriesQuery,
  useMarkKarmaExchangeRequestedCountriesSeenMutation,
} from "src/features/karma-exchanges/karma-exchanges.service";

const RequestedRewardCountriesPage = () => {
  const hasMarkedSeenRef = React.useRef(false);
  const [markRequestedCountriesSeen] = useMarkKarmaExchangeRequestedCountriesSeenMutation();
  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<PopulatedRequestedCountry>({
      useGetDataQuery: useGetKarmaExchangeRequestedCountriesQuery,
      useCountData: useCountKarmaExchangeRequestedCountriesQuery,
    });

  React.useEffect(() => {
    if (!isSuccess || hasMarkedSeenRef.current) {
      return;
    }

    hasMarkedSeenRef.current = true;
    markRequestedCountriesSeen();
  }, [isSuccess, markRequestedCountriesSeen]);

  return isSuccess ? (
    <div className="page_container">
      <PageHeader text="Reward Country Requests" />

      <div className="flex justify-center mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white">
          <Link
            to="/rewards"
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 no-underline"
          >
            Rewards
          </Link>
          <Link
            to="/rewards/requested-countries"
            className="px-4 py-2 text-sm font-medium bg-gray-900 text-white no-underline"
          >
            Country Requests
          </Link>
        </div>
      </div>

      {isLoading || isFetching ? (
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
            <thead className="bg-gray-800 text-white text-center">
              <tr>
                <th className="px-4 py-3">User Profile</th>
                <th className="px-4 py-3">Country Code</th>
                <th className="px-4 py-3">Created At</th>
                <th className="px-4 py-3">Updated At</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 10 }).map((_, index) => (
                <tr key={index} className="odd:bg-white even:bg-gray-50">
                  <td className="px-4 py-3 border-t border-gray-200">
                    <div className="flex items-center gap-3 animate-pulse">
                      <div className="w-10 h-10 rounded-full bg-gray-200" />
                      <div className="space-y-2">
                        <div className="h-4 w-32 bg-gray-200 rounded" />
                        <div className="h-3 w-40 bg-gray-100 rounded" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200 text-center">
                    <div className="h-4 w-16 bg-gray-200 rounded mx-auto animate-pulse" />
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200 text-center">
                    <div className="h-4 w-32 bg-gray-200 rounded mx-auto animate-pulse" />
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200 text-center">
                    <div className="h-4 w-32 bg-gray-200 rounded mx-auto animate-pulse" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <RequestedCountriesTable requests={data || []} />
      )}

      <Pagination
        numberOfPages={numberOfPages}
        currentPage={currentPage}
        prev={prev}
        next={next}
        isLoading={isLoading || isFetching}
      />
    </div>
  ) : isError ? (
    <Navigate to="/505" />
  ) : (
    <PageLoading />
  );
};

export default RequestedRewardCountriesPage;
